import React from 'react';
import CommonInput from '../../Common/CommonInput';

const PaymentSummarySection = ({
  grnAllocations = [],
  srnAllocations = [],
  debitNoteAllocations = [],
  adjustments = 0,
  creditNotesApplied = 0,
  onAdjustmentsChange,
  onCreditNotesChange,
  className = ''
}) => {
  // Calculate totals
  const totalPaymentAmount = grnAllocations.reduce((sum, allocation) => sum + (allocation.amountPaid || 0), 0);
  const totalSRNSetoff = srnAllocations.reduce((sum, allocation) => sum + (allocation.amountSetoff || 0), 0);
  const totalDebitNoteSetoff = debitNoteAllocations.reduce((sum, allocation) => sum + (allocation.amountSetoff || 0), 0);
  const netPaymentAmount = totalPaymentAmount + adjustments - creditNotesApplied - totalSRNSetoff - totalDebitNoteSetoff;

  const summaryItems = [
    {
      label: 'Total Payment Amount',
      value: totalPaymentAmount,
      description: 'Sum of all GRN allocations',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: 'Adjustments',
      value: adjustments,
      description: 'Additional adjustments (positive/negative)',
      color: adjustments >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: adjustments >= 0 ? 'bg-green-50' : 'bg-red-50',
      editable: true,
      onChange: onAdjustmentsChange
    },
    {
      label: 'Credit Notes Applied',
      value: creditNotesApplied,
      description: 'Credit notes applied to this payment',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      editable: true,
      onChange: onCreditNotesChange
    },
    {
      label: 'SRN Setoff Amount',
      value: totalSRNSetoff,
      description: 'Total supplier return note setoffs',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      label: 'Debit Note Setoff Amount',
      value: totalDebitNoteSetoff,
      description: 'Total debit note setoffs',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    {
      label: 'Net Payment Amount',
      value: netPaymentAmount,
      description: 'Final amount to be paid',
      color: 'text-gray-900',
      bgColor: 'bg-gray-50',
      isTotal: true
    }
  ];

  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      <div className="flex items-center bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
        <span className="text-xs font-semibold text-white">Payment Summary</span>
      </div>
      <div className="p-4">

      <div className="space-y-2">
        {summaryItems.map((item, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg border ${item.isTotal ? 'border-gray-300' : 'border-gray-200'} ${item.bgColor}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-1">
                  <h3 className={`text-sm font-medium ${item.color}`}>
                    {item.label}
                  </h3>
                  {item.isTotal && (
                    <span className="inline-flex px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                      Final
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {item.editable ? (
                  <div className="flex items-center space-x-1">
                    <div className="w-20">
                      <input
                        type="number"
                        value={item.value || 0}
                        onChange={(e) => item.onChange && item.onChange(parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                    <span className={`text-base font-semibold ${item.color}`}>
                      {parseFloat(item.value).toFixed(2)}
                    </span>
                  </div>
                ) : (
                  <span className={`text-lg font-bold ${item.color} ${item.isTotal ? 'text-xl' : ''}`}>
                    {parseFloat(item.value).toFixed(2)}
                  </span>
                )}
              </div>
            </div>

            {item.isTotal && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Payment: {parseFloat(totalPaymentAmount).toFixed(2)}</span>
                  <span>Adj: {parseFloat(adjustments).toFixed(2)}</span>
                  <span>Credits: {parseFloat(creditNotesApplied).toFixed(2)}</span>
                  <span>SRN: {parseFloat(totalSRNSetoff).toFixed(2)}</span>
                  <span>DN: {parseFloat(totalDebitNoteSetoff).toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Payment Breakdown */}
      {(grnAllocations.length > 0 || srnAllocations.length > 0) && (
        <div className="mt-3 space-y-3">
          {/* GRN Breakdown */}
          {grnAllocations.length > 0 && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-2">GRN Breakdown</h3>
              <div className="space-y-1">
                {grnAllocations.map((allocation, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{allocation.grnNo}</span>
                    <span className="font-medium text-gray-900">
                      {parseFloat(allocation.amountPaid || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-1 flex justify-between items-center text-sm font-medium">
                  <span className="text-gray-900">Total GRNs</span>
                  <span className="text-blue-600">{parseFloat(totalPaymentAmount).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* SRN Breakdown */}
          {srnAllocations.length > 0 && (
            <div className="p-3 bg-orange-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-2">SRN Setoff Breakdown</h3>
              <div className="space-y-1">
                {srnAllocations.map((allocation, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{allocation.srnNo}</span>
                    <span className="font-medium text-gray-900">
                      -{parseFloat(allocation.amountSetoff || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-1 flex justify-between items-center text-sm font-medium">
                  <span className="text-gray-900">Total SRN Setoffs</span>
                  <span className="text-orange-600">-{parseFloat(totalSRNSetoff).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Warning for negative net amount */}
      {netPaymentAmount < 0 && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-red-600 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-800">
              Warning: Negative net amount. Review adjustments and credits.
            </p>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PaymentSummarySection;